/**
 * SelectionTable — the ONE `SDataTable` selection recipe of the create wizard
 * (W12 Part B, Task B3).
 *
 * Owner ruling (2026-09-10): every selectable list in the create wizard is an
 * `SDataTable` — checkbox column ALWAYS visible (never the hover-revealed
 * `checkboxReveal` entry), header select-all, inline search from ten rows, its
 * own scroll container, no chip grids. The four steps (tasks, insight
 * variables, project variables, social proof) render this component with
 * different rows; the RULES (defaults, follow-the-tasks, path writes) stay in
 * `StrategyFormDialog`.
 *
 * Props-in / JSX-out: the table owns no selection state. `selectedIds` is the
 * truth (read off the orchestrator's path list) and every change is reported as
 * a fresh id list in ROW order.
 */

import React, { useCallback, useMemo } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import {
  SDataTable,
  type SDataTableColumnDef,
  type SDataTableRowSelectionState,
} from '@modules/ui-components/dialogs/SDataTable';

/** One selectable entity. `locked` rows travel with the request no matter what. */
export interface SelectionRow {
  id: string;
  name: string;
  detail?: string;
  locked?: boolean;
}

export interface SelectionTableProps<R extends SelectionRow = SelectionRow> {
  rows: R[];
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  /** Default: Name + Detail. */
  columns?: SDataTableColumnDef<R>[];
  /**
   * Rows that cannot ENTER the selection (a row already selected stays
   * removable — a control that cannot undo itself is a trap). Used by the
   * library table for broken examples and exhausted selection caps.
   */
  rowSelectable?: (row: R) => boolean;
  /**
   * Why a row cannot be selected. Named on the checkbox itself (tooltip +
   * `aria-description`) — designer invariant 14: never a silently disabled
   * control.
   */
  rowDisabledReason?: (row: R) => string | undefined;
  /** Whole table read-only (submit in flight). Select-all disables with it. */
  disabled?: boolean;
  /** Shown automatically once the table has ten rows or more. */
  searchPlaceholder?: string;
  /** Footer caption, e.g. «3 of 3 selected · 2 global». */
  summary: string;
  /**
   * One explanatory sentence UNDER the summary — the library step's «… open ⓘ
   * to see its screens, the rationale and the variable fit …» (approved canvas
   * v3.3). Omitted by every table whose columns already explain themselves.
   */
  footer?: string;
  loading?: boolean;
  error?: string | null;
  emptyState?: React.ReactNode;
  /** Height of the table's OWN scroll box. */
  maxHeight?: number;
  /**
   * W13 IS2 (REVIEW tier 2, MF-2): the scroll box starts at a 240px floor and
   * GROWS into the height the step has left instead of the fixed 434px, so a
   * long list uses the dialog and the summary/footer — which are rendered
   * OUTSIDE the scroll box — stay on screen at the 1280×800 floor. Every
   * ancestor up to the dialog's content box must be a growing flex column for
   * this to bite (the step root, the wizard column, the shell, and the dialog's
   * `contentFill`, which bounds the whole chain at the scroller's height); the
   * tables that do not ask for it keep the shipped fixed-height behaviour
   * byte-identical.
   */
  fillHeight?: boolean;
  /**
   * MRT's layout mode. `'grid'` lets a column `grow` into the free width — the
   * thesis table's Thesis column takes everything the four narrow columns leave
   * (MF-1). Omitted = MRT's default, i.e. every shipped table unchanged.
   */
  columnLayout?: 'grid' | 'grid-no-grow' | 'semantic';
  /** Accessible name of the table — a human step label, never the testid. */
  'aria-label': string;
  /**
   * Root id. The row / search / summary ids are this one plus the suffix the
   * registry documents (`STRATEGY_LIST.wizardTableRow|Search|Summary`), so the
   * table stays plane-agnostic while the ids stay registry-shaped.
   */
  'data-testid': string;
}

/** Search appears from this row count (owner ruling). */
export const SELECTION_TABLE_SEARCH_THRESHOLD = 10;
/** Default height of the table's own scroll box. */
export const SELECTION_TABLE_MAX_HEIGHT = 434;
/**
 * Floor of the `fillHeight` scroll box (MF-2): even in a short dialog the table
 * shows several rows rather than collapsing to its header. It is the box's
 * definite `height` (its flex base size) as well as its `min-height` — see the
 * container props for why a `min-height` alone could not hold.
 */
export const SELECTION_TABLE_MIN_FILL_HEIGHT = 240;

function SelectionTableInner<R extends SelectionRow>({
  rows,
  selectedIds,
  onSelectedIdsChange,
  columns,
  rowSelectable,
  rowDisabledReason,
  disabled = false,
  searchPlaceholder,
  summary,
  footer,
  loading = false,
  error = null,
  emptyState,
  maxHeight = SELECTION_TABLE_MAX_HEIGHT,
  fillHeight = false,
  columnLayout,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: SelectionTableProps<R>): React.ReactElement {
  const defaultColumns = useMemo<SDataTableColumnDef<R>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      Cell: ({ row }) => (
        <Typography
          variant="body2"
          color={row.original.locked ? 'text.secondary' : 'text.primary'}
        >
          {row.original.name}
        </Typography>
      ),
    },
    {
      accessorKey: 'detail',
      header: 'Detail',
      Cell: ({ row }) => (
        <Typography variant="body2" color="text.secondary">
          {row.original.detail ?? ''}
        </Typography>
      ),
    },
  ], []);

  const effectiveColumns = columns ?? defaultColumns;
  const searchEnabled = rows.length >= SELECTION_TABLE_SEARCH_THRESHOLD;

  /**
   * Locked rows read as selected whatever the caller passes — they are sent
   * regardless, and a table that showed them unticked would be lying (FP-7).
   */
  const rowSelection = useMemo<SDataTableRowSelectionState>(() => {
    const selected = new Set(selectedIds);
    const state: SDataTableRowSelectionState = {};
    for (const row of rows) {
      if (selected.has(row.id) || row.locked) state[row.id] = true;
    }
    return state;
  }, [rows, selectedIds]);

  const handleRowSelectionChange = useCallback((updater: unknown) => {
    const next = typeof updater === 'function'
      ? (updater as (prev: SDataTableRowSelectionState) => SDataTableRowSelectionState)(rowSelection)
      : (updater as SDataTableRowSelectionState);
    onSelectedIdsChange(rows.filter((row) => row.locked || next[row.id]).map((row) => row.id));
  }, [rows, rowSelection, onSelectedIdsChange]);

  const body = (() => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          {/* Page-owned spinner, never MRT's `state.isLoading` skeleton rows —
              those render rows whose `row.original` is empty. */}
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }
    if (rows.length === 0) {
      return emptyState ?? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          Nothing to choose here.
        </Typography>
      );
    }
    return (
      <SDataTable<R>
        columns={effectiveColumns}
        data={rows}
        getRowId={(row) => row.id}
        enableRowSelection={(row) => !disabled
          && !row.original.locked
          // Already selected → always removable, whatever the predicate says.
          && (rowSelection[row.original.id] === true || (rowSelectable?.(row.original) ?? true))}
        enableSelectAll
        muiSelectAllCheckboxProps={{ disabled }}
        muiSelectCheckboxProps={({ row }) => {
          const reason = rowDisabledReason?.(row.original);
          return {
            title: reason,
            inputProps: {
              'aria-label': `Select ${row.original.name}`,
              ...(reason ? { 'aria-description': reason } : {}),
            },
          };
        }}
        enableSorting={false}
        enablePagination={false}
        enableTopToolbar={searchEnabled}
        enableSearch={searchEnabled}
        searchInColumn="name"
        searchPlaceholder={searchPlaceholder}
        state={{ rowSelection }}
        {...(columnLayout ? { layoutMode: columnLayout } : {})}
        onRowSelectionChange={handleRowSelectionChange as never}
        // MF-2 (fix round 4): MRT wraps the toolbar and the scroll box in a
        // Paper that renders `display: block` — the ONE link that made the
        // flex declarations below inert (round 2 measured the box at 20 721px
        // at every viewport). In `fillHeight` mode the Paper is a flex column
        // that grows with the step, so the scroll box under it fills what the
        // step has. SDataTable's outlined/flat Paper is repeated here because
        // MRT REPLACES the paper props, it does not merge them.
        {...(fillHeight
          ? {
            muiTablePaperProps: {
              variant: 'outlined' as const,
              elevation: 0 as const,
              sx: { display: 'flex', flexDirection: 'column', flex: '1 1 auto' },
            },
          }
          : {})}
        // The table owns BOTH scroll axes: a column that cannot fit shows a
        // scrollbar inside the container and never clips a header against the
        // 800px column (Task B9d must-fix 2). MF-2: in `fillHeight` mode the
        // box STARTS at the floor and GROWS into the room the step has left
        // instead of a fixed 434px. The floor is a definite `height`, not only
        // a `min-height`: a scroll box's intrinsic height is its content's, so
        // with `height: auto` every ancestor's automatic minimum was the whole
        // list (the chain could never shrink) and with `min-height: 0` on
        // them the floor was violated and the Paper clipped the box to a
        // sliver. A definite floor makes every automatic minimum up the chain
        // exactly «the floor», so a step that cannot fit even that spills into
        // the dialog's scroller instead of clipping. `max-content` caps the
        // growth at the list itself, so a short list keeps its summary snug
        // under the last row.
        muiTableContainerProps={{
          sx: fillHeight
            ? {
              flex: '1 1 auto',
              height: SELECTION_TABLE_MIN_FILL_HEIGHT,
              minHeight: SELECTION_TABLE_MIN_FILL_HEIGHT,
              maxHeight: 'max-content',
              overflowX: 'auto',
            }
            : { maxHeight, overflowX: 'auto' },
        }}
        muiTableProps={{ size: 'small', 'aria-label': ariaLabel }}
        muiTableHeadCellProps={({ column }) => ({
          ...(searchEnabled && column.id === 'name'
            ? { 'data-testid': `${dataTestId}-search` }
            : {}),
          sx: {
            bgcolor: 'action.hover',
            borderBottom: '2px solid',
            borderColor: 'divider',
            fontWeight: 'fontWeightMedium',
          },
        })}
        muiTableBodyRowProps={({ row }) => ({
          hover: true,
          'data-testid': `${dataTestId}-row-${row.original.id}`,
        })}
      />
    );
  })();

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        // MF-2: the table grows, the summary and the footer do not — they sit
        // after the scroll box and therefore stay visible at every height.
        // Every link from here down to the scroll box is a growing flex
        // column with its AUTOMATIC minimum (never `minHeight: 0`): the scroll
        // box's definite floor is what that minimum resolves to.
        ...(fillHeight ? { flex: '1 1 auto' } : {}),
      }}
    >
      {fillHeight
        ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
            {body}
          </Box>
        )
        : body}
      <Typography
        variant="caption"
        color="text.secondary"
        data-testid={`${dataTestId}-summary`}
      >
        {summary}
      </Typography>
      {footer && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={`${dataTestId}-footer`}
        >
          {footer}
        </Typography>
      )}
    </Box>
  );
}

export const SelectionTable = React.memo(SelectionTableInner) as typeof SelectionTableInner;
(SelectionTable as unknown as { displayName: string }).displayName = 'SelectionTable';
